import React, { useEffect } from 'react';
import { PageHeader } from '../components/page/PageHeader';
import { FeatureRow } from '../components/page/FeatureRow';
import { CtaBand } from '../components/page/CtaBand';
import { Counter } from '../components/ui/Counter';
import { Reveal } from '../components/ui/Reveal';
import { PlayCircle } from 'lucide-react';
import { LayeredRosterDatabaseMockup, LayeredCastingCallMockup, LayeredDirectoryMockup, LayeredAListMockup } from '../components/page/BrandMockups';
import { HowMarketplaceWorks } from '../components/page/HowMarketplaceWorks';
import { Testimonials, BRAND_TESTIMONIALS } from '../components/Testimonials';
const DEMO_VIDEO_URL = 'https://www.youtube.com/watch?v=U2sV81k41u4&pp=0gcJCUwLAYcqIYzv';
const SEO = {
  title: 'Discover Represented Creators & Talent Agency Rosters | Zano for Brands',
  description: 'Access a live database of talent agency rosters to discover represented creators, compare talent agencies, connect with talent managers, and launch influencer campaigns faster.',
  keywords: 'represented creators, discover talent agencies, talent agency rosters, live roster database, talent agency directory, influencer discovery, influencer talent agencies, creator partnerships',
  ogTitle: 'For Brands | Discover Represented Influencers through Talent Agencies on Zano',
  ogDescription: 'Find represented creators through verified talent agencies, browse live rosters, and source influencers faster with real-time creator data.'
};
function updateMetaTag(attribute: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attribute}="${key}"]`;
  const existingTag = document.head.querySelector<HTMLMetaElement>(selector);
  const tag = existingTag ?? document.createElement('meta');
  const previousContent = existingTag?.getAttribute('content') ?? null;
  if (!existingTag) {
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
  return () => {
    if (!existingTag) {
      tag.remove();
      return;
    }
    if (previousContent === null) {
      existingTag.removeAttribute('content');
    } else {
      existingTag.setAttribute('content', previousContent);
    }
  };
}
export const ForBrands = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = SEO.title;
    const restoreDescription = updateMetaTag('name', 'description', SEO.description);
    const restoreKeywords = updateMetaTag('name', 'keywords', SEO.keywords);
    const restoreOgTitle = updateMetaTag('property', 'og:title', SEO.ogTitle);
    const restoreOgDescription = updateMetaTag('property', 'og:description', SEO.ogDescription);
    return () => {
      document.title = previousTitle;
      restoreDescription();
      restoreKeywords();
      restoreOgTitle();
      restoreOgDescription();
    };
  }, []);
  return <>
      <PageHeader eyebrow="For Brands" eyebrowClass="bg-blue-50 text-blue-700 border border-blue-200" title="Partner With talent agencies to discover represented creators" subtitle="Zano makes it accessible and scalable to book elite talent. It's no longer gate-kept behind closed doors or limited to your personal network. Anyone can post a casting call, but approval is required to access the premium roster database and agency directory." primaryCta={{
      label: 'Start Free Trial',
      to: '/pricing'
    }} secondaryCta={{
      label: 'Watch demo video',
      href: DEMO_VIDEO_URL,
      newTab: true,
      icon: <PlayCircle size={18} className="mr-1.5" data-id="element-2553" data-mp="2092hlwytuscf" />
    }} data-id="element-2552" data-mp="13e9lxnpkde7w" />

      {/* Metrics strip */}
      <section className="border-y border-gray-100 bg-gray-50/60 py-10" data-id="element-2554" data-mp="1w6z472slldoi">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-2555" data-mp="1r7w13nw6hhj6">
          {[{
          v: <Counter to={400} suffix="+" data-id="element-2556" data-mp="3lk2l9c71v8nm" />,
          l: 'Talent agencies'
        }, {
          v: <Counter to={6000} suffix="+" data-id="element-2557" data-mp="2i8n7vzrw7yc1" />,
          l: 'Represented creators'
        }, {
          v: <Counter to={100} suffix="" data-id="element-2558" data-mp="2n76vs5q1rk71" />,
          l: 'Avg submissions / 24h'
        }, {
          v: <Counter to={500} prefix="$" suffix="k+" data-id="element-2559" data-mp="297yqcv3ajfue" />,
          l: 'GMV generated'
        }].map((s, i) => <Reveal key={i} delay={i * 0.08} data-id="element-2560" data-mp="2e9eamkxn9ibq" data-mpi={i}>
              <p className="text-3xl font-bold text-zano-pink" data-id="element-2561" data-mp="1zhm7r8f1dwqw">{s.v}</p>
              <p className="text-sm text-gray-600 mt-1" data-id="element-2562" data-mp="24942heqgv13f">{s.l}</p>
            </Reveal>)}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 divide-y divide-gray-100" data-id="element-2563" data-mp="1qj0i6tb5dhgm">
        <FeatureRow eyebrow="Accessible Discovery" title="Search a live roster database" body="Reach far beyond your personal network. Search 6,000+ represented creators across 400+ agencies in one place, with live performance data kept current by Zano." bullets={['Filter by followers, engagement rate, and location', 'See live stats for TikTok, YouTube, and Instagram', 'Find direct contact info for talent managers']} visual={<LayeredRosterDatabaseMockup data-id="element-2565" data-mp="1bw1t34zriz52" />} data-id="element-2564" data-mp="1uisxdov1qluy" />
        <FeatureRow eyebrow="Direct Access" title="Partner with A-list talent instantly" body="When you want to partner with A-list talent like Charli D'Amelio, Tessa Brooks, or Amanda Cerny, you can just search them in the database. See exactly who they are represented by, along with their talent manager's specific email address and contact phone number. Booking A-list talent is quick and efficient in the right network, and we keep growing our talent agency supply." visual={<LayeredAListMockup data-id="element-2567" data-mp="23ly9605gtp6p" />} flip data-id="element-2566" data-mp="16w763cj3ytpa" />
        <FeatureRow eyebrow="Scalable Sourcing" title="Get custom submissions in hours" body="Post a campaign brief specifying your goal, deliverables, and budget. It blasts instantly to 400+ talent agencies who submit their best fit, scaling your outreach instantly." bullets={['Receive ~100 custom submissions within 24 hours', 'Review custom quotes and notes from managers', 'Filter submissions and take the conversation to email']} visual={<LayeredCastingCallMockup data-id="element-2569" data-mp="2pessvq4t342s" />} data-id="element-2568" data-mp="23mpwknowsglw" />
        <FeatureRow eyebrow="Open Doors" title="The talent agency directory" body="See exactly who represents who. Browse the directory to view high-level rosters and find the right point of contact for the industry's top agencies." bullets={['List of all talent agencies on Zano', 'High-level view of their roster size', 'Direct access to agency websites and general POCs']} visual={<LayeredDirectoryMockup data-id="element-2571" data-mp="19x0jjf3chg1x" />} flip data-id="element-2570" data-mp="2pe0wid3dx2bg" />
      </div>

      <HowMarketplaceWorks audience="brands" data-id="element-2572" data-mp="3n7titykt4ddv" />

      <Testimonials title="What brands and agencies are saying" items={BRAND_TESTIMONIALS} data-id="element-2573" data-mp="3n9mr1ib2j5nz" />
      <CtaBand title="Elite talent, open to everyone" subtitle="Join the brands using Zano to access and scale their elite talent bookings." primaryCta={{
      label: 'Start Free Trial',
      to: '/pricing'
    }} secondaryCta={{
      label: 'Watch demo video',
      href: DEMO_VIDEO_URL,
      newTab: true,
      icon: <PlayCircle size={18} className="mr-1.5" data-id="element-2575" data-mp="619iwmyv4pvl" />
    }} data-id="element-2574" data-mp="3ig22shza1q1z" />
    </>;
};